import React, { useState, useEffect } from 'react';

const ServiceStatusWidget = ({ title, services = [], config = {} }) => {
  // Get config thresholds (in minutes)
  const healthyMinutes = config.healthyMinutes || 5;
  const warningMinutes = config.warningMinutes || 15;
  const staleMinutes = config.staleMinutes || 30;

  // Force re-render every 30 seconds to update time-based status
  const [, setUpdateTrigger] = useState(0);

  useEffect(() => {
    // Update every 30 seconds to recalculate staleness
    const interval = setInterval(() => {
      setUpdateTrigger(prev => prev + 1);
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, []);

  // Find the most recent update timestamp
  const getLastUpdateTime = () => {
    if (!services || services.length === 0) return null;

    const timestamps = services
      .map(s => s.lastCheck)
      .filter(t => t)
      .map(t => new Date(t));

    if (timestamps.length === 0) return null;
    return new Date(Math.max(...timestamps));
  };

  const lastUpdate = getLastUpdateTime();

  const getStatusBadgeClass = (service) => {
    const statusLower = service.status?.toLowerCase() || '';
    const lastCheck = service.lastCheck ? new Date(service.lastCheck) : null;
    const now = new Date();

    // If no timestamp, use the status from API
    if (!lastCheck) {
      if (statusLower === 'running') return 'status-badge running';
      if (statusLower === 'stopped' || statusLower === 'error') return 'status-badge stopped';
      return 'status-badge warning';
    }

    const minutesSinceUpdate = (now - lastCheck) / 1000 / 60;

    // If status is "Stopped" from API, always show as stopped
    if (statusLower === 'stopped' || statusLower === 'error') {
      return 'status-badge stopped';
    }

    // Time-based logic for "Running" status
    if (statusLower === 'running') {
      if (minutesSinceUpdate <= healthyMinutes) {
        return 'status-badge running'; // Green - recently updated
      } else if (minutesSinceUpdate <= warningMinutes) {
        return 'status-badge warning'; // Orange - no recent update
      } else {
        return 'status-badge stopped'; // Red - stale data
      }
    }

    return 'status-badge warning';
  };

  const getDisplayStatus = (service) => {
    const statusLower = service.status?.toLowerCase() || '';
    const lastCheck = service.lastCheck ? new Date(service.lastCheck) : null;
    const now = new Date();

    if (!lastCheck) return service.status;

    const minutesSinceUpdate = (now - lastCheck) / 1000 / 60;

    // If status is explicitly "Stopped", show it
    if (statusLower === 'stopped' || statusLower === 'error') {
      return service.status;
    }

    // Time-based status for "Running"
    if (statusLower === 'running') {
      if (minutesSinceUpdate <= healthyMinutes) {
        return 'Running';
      } else if (minutesSinceUpdate <= warningMinutes) {
        return 'Stale';
      } else {
        return 'No Data';
      }
    }

    return service.status;
  };

  return (
    <div className="widget table-widget">
      <div className="widget-header">
        <span className="widget-title">{title}</span>
      </div>
      <div className="widget-body" style={{ justifyContent: 'flex-start', alignItems: 'stretch' }}>
        {lastUpdate && (
          <div style={{
            padding: '0.5rem 0.75rem',
            fontSize: '0.8rem',
            color: 'var(--widget-text-secondary)',
            borderBottom: '1px solid var(--widget-border)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span>Last Update:</span>
            <span style={{ fontWeight: '500' }}>{lastUpdate.toLocaleTimeString()}</span>
          </div>
        )}
        <table className="widget-table">
          <thead>
            <tr>
              <th>Service</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {services.length > 0 ? (
              services.map((service, index) => (
                <tr key={index}>
                  <td>{service.serviceName || service.service_name || service.name}</td>
                  <td>
                    <span className={getStatusBadgeClass(service)}>
                      {getDisplayStatus(service)}
                    </span>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={2} style={{ textAlign: 'center', color: 'var(--widget-text-secondary)' }}>
                  No services
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ServiceStatusWidget;
